import type React from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "~/constants/messages";
import { ProjectApi } from "~/functions/api/project";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

interface DeleteProjectProps {
  onClose: (shouldRefresh?: boolean) => void;
  projectName: string;
  projectId: string;
  onConfirm?: (() => Promise<void> | void) | undefined;
  confirmLabel?: string | undefined;
  cancelLabel?: string | undefined;
  description?: React.ReactNode | undefined;
}

const DeleteProjectModal: React.FC<DeleteProjectProps> = ({
  onClose,
  projectName,
  projectId,
  onConfirm,
  confirmLabel,
  cancelLabel,
  description,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const confirmButtonClass = description ? styles["hideButton"] : styles["deleteButton"];

  const handleConfirm = async () => {
    setIsLoading(true);

    if (onConfirm) {
      try {
        await onConfirm();
      } finally {
        setIsLoading(false);
      }
      return;
    }

    const projectApi = new ProjectApi();

    const response = await projectApi.delete(currentUser?.organization_id, projectId);

    if (response !== null) {
      setIsLoading(false);
      onClose();
      toast.success(TOAST_MESSAGES.PROJECT.DELETE_SUCCESS);
      setTimeout(() => {
        navigate(`/${currentUser.organization_id}/home`);
      }, 2000);
    } else {
      setIsLoading(false);
    }
  };
  return (
    <div className={styles["modalOverlay"]}>
      <div className={styles["modalContent"]}>
        <div className={styles["modalHeader"]}>
          <div className={styles["modalTitle"]}>
            <h2>プロジェクトの削除</h2>
            <h4>
              プロジェクト名： <strong>{projectName}</strong>
            </h4>
            {description ? (
              <div className={styles["customDescription"]}>{description}</div>
            ) : (
              <div className={styles["modalWarning"]}>
                <span className={styles["warningIcon"]}>⚠️</span>
                <p>
                  ※この操作は取り消せません <br />
                  これまで生成したコンテンツを復元することはできません
                </p>
              </div>
            )}
            <div className={styles["actions"]}>
              <button
                className={styles["cancelButton"]}
                onClick={() => onClose()}
                disabled={isLoading}
              >
                {cancelLabel ?? "キャンセル"}
              </button>
              <button className={confirmButtonClass} onClick={handleConfirm} disabled={isLoading}>
                {confirmLabel ?? "削除"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteProjectModal;
